/**
 * SEO Component
 * Drop into any page to set meta tags, Open Graph, and JSON-LD structured data.
 * No duplication — one component handles all SEO concerns.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: 'website' | 'product';
  product?: {
    name: string;
    description: string;
    image: string;
    priceCents: number;
    currency?: string;
    availability?: 'InStock' | 'OutOfStock';
    sku?: string;
    category?: string;
  };
}

const BASE_URL = 'https://micahlynise.com';
const SITE_NAME = 'Micah Lynise';
const DEFAULT_DESCRIPTION = 'Original fine art, handmade natural fragrances, and body care. Small-batch, crafted in Texas.';
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;

const SEO: React.FC<SEOProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  product,
}) => {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Art, Fragrance & Body`;
  const canonicalUrl = `${BASE_URL}${path}`;
  const fullImage = image.startsWith('http') ? image : `${BASE_URL}${image}`;

  // Product JSON-LD structured data
  const productJsonLd = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description,
        image: product.image.startsWith('http') ? product.image : `${BASE_URL}${product.image}`,
        sku: product.sku || undefined,
        category: product.category || undefined,
        brand: {
          '@type': 'Brand',
          name: SITE_NAME,
        },
        offers: {
          '@type': 'Offer',
          url: canonicalUrl,
          priceCurrency: product.currency || 'USD',
          price: (product.priceCents / 100).toFixed(2),
          availability: `https://schema.org/${product.availability || 'InStock'}`,
          seller: {
            '@type': 'Organization',
            name: SITE_NAME,
          },
        },
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Product structured data */}
      {productJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(productJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
